// The left rail: everything the staff and the units have said, newest first. Each card is a template
// filled from one simulation event (src/ui/reports.ts); the periodic situation report is the feed's own,
// built from the snapshot every fifteen sim minutes. `src/ui/feed/triage.ts` then groups same-kind
// traffic within a sim minute and ranks the rest by severity (spec §5 "feed triage", 2026-09-21
// pitches) - the independent review's finding 5: "135 cards in two sim hours, six identical 'pinned'
// cards at one timestamp."

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Contact, Event, Unit } from '../../sim/types';
import { useStore } from '../../state/store';
import { useFeed } from '../../state/feed';
import { Acronym } from '../Acronym';
import { EventCard } from '../cards/EventCard';
import { Chip } from '../cards/ui';
import { pingCell } from '../ping';
import { SITREP_INTERVAL_TICKS, reportText, sitrepReport, type Report, type ReportCtx } from '../reports';
import { FEED_FILTERS, loadFeedFilter, matchesFilter, saveFeedFilter, triageEvents, type FeedFilter, type FeedItem } from './triage';

/** How many reports the rail keeps mounted; older ones stay in the event log for the debrief. */
const MAX_CARDS = 160;

/** How many triaged rows the rail actually renders at once. Not the same knob as `MAX_CARDS`: that one
 * bounds the raw event cache this component keeps; this one bounds what gets mounted into the DOM, after
 * grouping and filtering, so "show earlier" reveals rows the browser was never asked to build until now. */
const VISIBLE_CAP = 150;

const FILTER_LABEL: Record<FeedFilter, string> = { all: 'All', significant: 'Significant', orders: 'Orders' };

export function ReportFeed() {
  const events = useStore((s) => s.events);
  const notices = useStore((s) => s.notices);
  const snapshot = useStore((s) => s.snapshot);
  const grid = useStore((s) => s.grid);
  const enrichments = useFeed((s) => s.enrichments);
  const pending = useFeed((s) => s.pending);
  const enrich = useFeed((s) => s.enrich);
  const [sitreps, setSitreps] = useState<Report[]>([]);
  // Negative start so the very first snapshot opens the feed with a situation report rather than silence.
  const lastSitrepTick = useRef(-SITREP_INTERVAL_TICKS);
  const scroller = useRef<HTMLDivElement>(null);
  const prevHeight = useRef(0);

  const [filter, setFilterState] = useState<FeedFilter>(() => loadFeedFilter());
  const setFilter = useCallback((f: FeedFilter) => {
    setFilterState(f);
    saveFeedFilter(f);
  }, []);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => setShowAll(false), [filter]);

  // The staff's own periodic report. It is generated here rather than in the simulation so it can
  // summarise what the player can see (blue's picture), not ground truth. It also gets an LLM
  // enrichment like any significant contact event, so it needs a matching synthetic `Event`: the
  // enrichment is keyed by id, and `sitrepReport` already mints a stable negative id per tick.
  useEffect(() => {
    if (!snapshot) return;
    if (snapshot.tick - lastSitrepTick.current < SITREP_INTERVAL_TICKS) return;
    lastSitrepTick.current = snapshot.tick;
    const next = sitrepReport(snapshot);
    setSitreps((prev) => [...prev.slice(-20), next]);
    enrich({ id: next.id, t: next.t, kind: 'sitrep', side: 0, payload: { text: next.text }, significant: true });
  }, [snapshot, enrich]);

  // The report context is whatever the staff knows right now. It is read when an event is first turned
  // into a report, so it lives in a ref instead of driving a re-render ten times a second.
  const ctxRef = useRef<ReportCtx>({ units: new Map(), contacts: new Map(), grid: null, tick: 0 });
  if (snapshot) {
    const units = new Map<number, Unit>();
    const contacts = new Map<number, Contact>();
    for (const u of snapshot.units) units.set(u.id, u);
    for (const c of snapshot.picture.contacts) contacts.set(c.enemyId, c);
    ctxRef.current = { ...ctxRef.current, units, contacts, grid, tick: snapshot.tick };
  }

  // Last-known type per enemy id, kept alive after `src/sim/detection.ts` drops a stale contact from the
  // picture, so a `contact.lost` report can still say what was lost. Grown incrementally in the reports
  // loop below rather than rebuilt from the whole log every render.
  const enemyTypesRef = useRef<Map<number, string>>(new Map());
  ctxRef.current.enemyTypes = enemyTypesRef.current;

  // Also builds the id -> cell lookup a citation chip needs to ping the right spot: a cited event's own
  // cell, not the report's.
  const cellById = useMemo(() => {
    const m = new Map<number, number | undefined>();
    for (const e of events) m.set(e.id, e.cell);
    return m;
  }, [events]);
  // Stable across renders so the memoized `EventCard`s are not invalidated by a new closure every
  // time a snapshot arrives (ten times a second, a hundred and sixty cards).
  const onCiteHover = useCallback(
    (id: number | null): void => pingCell(id === null ? null : (cellById.get(id) ?? null)),
    [cellById],
  );

  // Each event becomes a report once, when it arrives, and is then kept: re-templating the whole log on
  // every snapshot would be thousands of string builds a second at 4x. The same pass triggers LLM
  // enrichment for every significant contact event (`useFeed.enrich` is a no-op for anything else).
  const cache = useRef<{ lastId: number; list: Report[] }>({ lastId: 0, list: [] });
  // Events seen this render that still have to be handed to the enrichment queue. `useFeed.enrich`
  // calls `set()` on its own store, and calling it from inside the memo below is a state update during
  // another component's render (React warns, loudly and correctly); the effect underneath drains this.
  const toEnrich = useRef<Event[]>([]);
  const reports = useMemo(() => {
    const c = cache.current;
    for (const ev of events) {
      if (ev.id <= c.lastId) continue;
      c.lastId = ev.id;
      if (ev.kind === 'contact.new') {
        const enemyId = ev.payload.enemyId;
        const typeConf = ev.payload.typeConf;
        if (typeof enemyId === 'number' && typeof typeConf === 'string') enemyTypesRef.current.set(enemyId, typeConf);
      }
      toEnrich.current.push(ev);
      const r = reportText(ev, ctxRef.current);
      if (r) c.list.push(r);
    }
    if (c.list.length > MAX_CARDS) c.list = c.list.slice(-MAX_CARDS);
    // Staff notices (the planning clock running out) are stamped at H+00:00 and sit at the head of the
    // run with the opening SITREP, which is where they happened.
    const noticeCards: Report[] = notices.map((n) => ({
      id: n.id, t: n.t, kind: 'phase', source: n.source, text: n.text, significant: true,
    }));
    return [...c.list, ...sitreps, ...noticeCards].sort((a, b) => (a.t === b.t ? a.id - b.id : a.t - b.t));
    // `enrich` is called from the effect below, not from here, so it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, sitreps, notices]);

  useEffect(() => {
    if (toEnrich.current.length === 0) return;
    const queue = toEnrich.current;
    toEnrich.current = [];
    for (const ev of queue) enrich(ev);
  }, [reports, enrich]);

  // Group same-kind traffic within a sim minute and rank the rest by severity - critical first within a
  // minute, newest minute first overall (`src/ui/feed/triage.ts`). Runs on the already-capped, already-
  // deduplicated report list, so it is a few hundred small objects at worst, not the whole event log.
  const allItems = useMemo(() => triageEvents(reports), [reports]);
  const filteredItems = useMemo(() => allItems.filter((it) => matchesFilter(it, filter)), [allItems, filter]);
  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, VISIBLE_CAP);
  const hiddenCount = filteredItems.length - visibleItems.length;

  // The newest significant card gets one second of extra attention on arrival (opacity only - nothing in
  // this interface moves to get attention, src/styles.css `.wt-pulse`'s own rule). Tracked by id so a
  // snapshot tick that leaves the top card unchanged never re-triggers it.
  const lastTopId = useRef<number | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  useEffect(() => {
    const top = visibleItems[0];
    if (!top || lastTopId.current === top.id) return;
    lastTopId.current = top.id;
    if (!top.significant) return;
    setHighlightId(top.id);
    const timer = setTimeout(() => setHighlightId((cur) => (cur === top.id ? null : cur)), 1000);
    return () => clearTimeout(timer);
  }, [visibleItems]);

  // Newest is at the top, so the feed only has to hold its scroll position when the reader has scrolled
  // down into the older reports.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const grew = el.scrollHeight - prevHeight.current;
    if (el.scrollTop > 8 && grew > 0) el.scrollTop += grew;
    prevHeight.current = el.scrollHeight;
  }, [visibleItems]);

  return (
    <section data-testid="feed" className="flex h-full min-h-0 flex-col">
      <header className="wt-panel-head">
        <span>Report feed</span>
        <span className="tracking-normal">{filteredItems.length}</span>
      </header>
      <div className="flex items-center gap-1 border-b border-slate-700/60 px-2 py-1.5" data-testid="feed-filters">
        {FEED_FILTERS.map((f) => (
          <Chip key={f} testId={`feed-filter-${f}`} active={filter === f} onClick={() => setFilter(f)}>
            {FILTER_LABEL[f]}
          </Chip>
        ))}
      </div>
      <p className="mono border-b border-slate-700/60 px-2 py-1 text-[9px] text-fog" data-testid="contact-ring-legend">
        Red circle: position uncertainty. Green broken ring: role uncertainty; larger means less certain.
      </p>
      <div ref={scroller} className="thin-scroll min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {visibleItems.length === 0 && (
          <div className="wt-empty" data-testid="feed-empty">
            {allItems.length === 0 ? (
              <>
                No reports yet — the first <Acronym k="sitrep">SITREP</Acronym> arrives at H+00:00.
                <span className="mt-1.5 block text-slate-700">
                  Everything the staff and the platoons say lands here, newest first.
                </span>
              </>
            ) : (
              <>Nothing on the {FILTER_LABEL[filter].toLowerCase()} filter yet.</>
            )}
          </div>
        )}
        {visibleItems.map((item: FeedItem) => (
          <EventCard
            key={item.id}
            item={item}
            onHover={pingCell}
            enrichment={enrichments[item.id]}
            pending={pending.has(item.id)}
            onCiteHover={onCiteHover}
            highlight={item.id === highlightId}
          />
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            data-testid="feed-show-earlier"
            onClick={() => setShowAll(true)}
            className="mono w-full rounded-sm border border-slate-700 py-1 text-[10px] uppercase tracking-[0.12em] text-fog hover:text-paper"
          >
            Show {hiddenCount} earlier
          </button>
        )}
      </div>
    </section>
  );
}
